import { useRef } from 'react'

import { Svg } from '../../primitives'

import type { ReactNode } from 'react'
import type { DeckDefinition, DeckSlot } from '@opentrons/shared-data'
import type { SvgProps } from '../../primitives'

export interface RobotCoordinateSpaceWithRefRenderProps {
  deckSlotsById: { [slotId: string]: DeckSlot }
}

interface RobotCoordinateSpaceWithRefProps extends SvgProps {
  viewBox?: string
  deckDef?: DeckDefinition
  zoomed?: boolean
  adjustViewBoxForStacker?: boolean
  children?: (props: RobotCoordinateSpaceWithRefRenderProps) => ReactNode
}

// manual visual adjustments for flex stacker deck view to fit properly and
// in the center of the frame
const STACKER_VIEWBOX_ADJUSTMENTS = {
  viewBoxOriginX: 260,
  viewBoxOriginY: -25,
  deckXDimension: -255,
  deckYDimension: 105,
}

export function RobotCoordinateSpaceWithRef(
  props: RobotCoordinateSpaceWithRefProps
): JSX.Element | null {
  const {
    children,
    deckDef,
    viewBox,
    adjustViewBoxForStacker = false,
    zoomed = false,
    ...restProps
  } = props
  const wrapperRef = useRef<SVGSVGElement>(null)

  if (deckDef == null && viewBox == null) return null

  let wholeDeckViewBox
  let deckSlotsById = {}
  if (deckDef != null) {
    const [viewBoxOriginX, viewBoxOriginY] = deckDef.cornerOffsetFromOrigin
    const [deckXDimension, deckYDimension] = deckDef.dimensions

    deckSlotsById = deckDef.locations.addressableAreas.reduce(
      (acc, deckSlot) => ({ ...acc, [deckSlot.id]: deckSlot }),
      {}
    )
    const base = [
      viewBoxOriginX,
      viewBoxOriginY,
      deckXDimension,
      deckYDimension,
    ] as const
    const adj = [
      base[0] + STACKER_VIEWBOX_ADJUSTMENTS.viewBoxOriginX,
      base[1] + STACKER_VIEWBOX_ADJUSTMENTS.viewBoxOriginY,
      base[2] + STACKER_VIEWBOX_ADJUSTMENTS.deckXDimension,
      base[3] + STACKER_VIEWBOX_ADJUSTMENTS.deckYDimension,
    ] as const

    const adjustedViewBox = adjustViewBoxForStacker ? adj : base
    wholeDeckViewBox = `${adjustedViewBox[0]} ${adjustedViewBox[1]} ${adjustedViewBox[2]} ${adjustedViewBox[3]}`
  }

  return (
    <Svg
      viewBox={zoomed ? viewBox : wholeDeckViewBox}
      ref={wrapperRef}
      transform="scale(1, -1)"
      width="100%"
      height="100%"
      {...restProps}
    >
      {children?.({ deckSlotsById })}
    </Svg>
  )
}
